import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import ClientFields from "../components/ClientFields";
import CostTable from "../components/CostTable";
import SummaryCard from "../components/SummaryCard";
import NotesCard from "../components/NotesCard";
import { api, emptyCosts } from "../lib/api";
import { Save } from "lucide-react";

export default function NewOrder() {
  const nav = useNavigate();
  const [values, setValues] = useState({
    name: "",
    client_name: "",
    client_phone: "",
  });
  const [costs, setCosts] = useState(emptyCosts());
  const [customItems, setCustomItems] = useState([]);
  const [salePrice, setSalePrice] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const totalCosts =
    Object.values(costs).reduce((s, v) => s + Number(v || 0), 0) +
    customItems.reduce((s, it) => s + Number(it?.amount || 0), 0);

  const submit = async () => {
    if (!values.name?.trim()) {
      toast.error("Podaj nazwę zlecenia");
      return;
    }
    setSaving(true);
    try {
      // For new (non-quote) order: planned costs are entered here.
      // Actual costs start empty - filled in on the order detail page.
      const res = await api.post("/orders", {
        ...values,
        costs,
        custom_items: customItems,
        sale_price: Number(salePrice || 0),
        notes,
      });
      toast.success("Zlecenie utworzone");
      nav(`/zlecenia/${res.data.id}`);
    } catch (e) {
      toast.error("Nie udało się utworzyć zlecenia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-order-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
          Nowe zlecenie
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
          Załóż nowe zlecenie
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-2">
          Wpisz dane klienta i planowane koszty. Po utworzeniu zlecenia będziesz mógł osobno wpisywać rzeczywiste koszty.
        </p>
      </header>

      <ClientFields values={values} onChange={setValues} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CostTable
            costs={costs}
            customItems={customItems}
            onChange={setCosts}
            onChangeCustomItems={setCustomItems}
            title="Wycena (planowane koszty)"
            accent="var(--primary)"
          />
        </div>
        <div>
          <SummaryCard
            salePrice={salePrice}
            totalCosts={totalCosts}
            onChangeSalePrice={setSalePrice}
          />
        </div>
      </div>

      <NotesCard value={notes} onChange={setNotes} />

      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => nav("/")}
          data-testid="cancel-btn"
        >
          Anuluj
        </Button>
        <Button
          onClick={submit}
          disabled={saving}
          data-testid="create-order-btn"
          className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
        >
          <Save size={16} className="mr-2" />
          {saving ? "Zapisywanie..." : "Utwórz zlecenie"}
        </Button>
      </div>
    </div>
  );
}
